import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, CardContent, Grid, Stack, TextField, Typography } from '@mui/material';
import { Stars } from '@mui/icons-material';
import api from '../../services/api';
import { getApiErrorMessage } from '../../services/userService';
import { formatMoney } from '../../utils/currency';

type PointsSummary = {
  enabled: boolean;
  balance: number;
  lifetimePoints: number;
  tier?: string;
  tiersEnabled?: boolean;
  currency: { code: string; symbol: string };
  moneyValue: number;
  expiryDays: number;
  expiringSoon?: { points: number; firstExpiry?: string | null };
  conversion: {
    enabled: boolean;
    allowManual: boolean;
    pointsPerUnit: number;
    minPoints: number;
    convertibleNow: number;
    canConvert: boolean;
  };
  history: Array<{ _id: string; type: string; points: number; description?: string; createdAt: string }>;
};

export default function MyPointsView() {
  const queryClient = useQueryClient();
  const [points, setPoints] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-points'],
    queryFn: async () => (await api.get('/rewards/points')).data.data as PointsSummary,
  });

  const convert = useMutation({
    mutationFn: (value: number) => api.post('/rewards/points/convert', {
      points: value,
      idempotencyKey: `web-${Date.now()}`,
    }),
    onSuccess: () => {
      setPoints('');
      queryClient.invalidateQueries({ queryKey: ['my-points'] });
    },
  });

  const money = (v: number) => formatMoney(v, { symbol: data?.currency.symbol, currency: data?.currency.code });
  const requested = Math.floor(Number(points) || 0);
  const preview = data && data.conversion.pointsPerUnit > 0
    ? Math.floor((requested / data.conversion.pointsPerUnit) * 100) / 100
    : 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>My Points</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error, 'Could not load points')}</Alert>}
      {isLoading && <Typography color="text.secondary">Loading…</Typography>}
      {data && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Stars color="warning" />
                  <Typography variant="overline">{data.tiersEnabled && data.tier ? `${data.tier} tier` : 'Balance'}</Typography>
                </Stack>
                <Typography variant="h3" fontWeight={700}>{data.balance.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Worth {money(data.moneyValue)}</Typography>
                <Typography variant="caption" display="block" mt={1}>Lifetime: {data.lifetimePoints.toLocaleString()} pts</Typography>
                {data.expiringSoon && data.expiringSoon.points > 0 && (
                  <Typography variant="caption" color="warning.main" display="block">
                    {data.expiringSoon.points} points expire soon
                  </Typography>
                )}
                {data.conversion.enabled && data.conversion.allowManual && (
                  <Stack spacing={1} mt={2}>
                    {convert.error && <Alert severity="error">{getApiErrorMessage(convert.error, 'Conversion failed')}</Alert>}
                    {convert.isSuccess && <Alert severity="success">Points added to your wallet.</Alert>}
                    <TextField
                      size="small"
                      type="number"
                      label={`Points to convert (min ${data.conversion.minPoints})`}
                      value={points}
                      onChange={(e) => setPoints(e.target.value)}
                      helperText={requested > 0 ? `You get ${money(preview)}` : `${data.conversion.pointsPerUnit} points = ${money(1)}`}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => setPoints(String(data.conversion.convertibleNow))} disabled={!data.conversion.convertibleNow}>
                        Max ({data.conversion.convertibleNow})
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!data.conversion.canConvert || requested < data.conversion.minPoints || requested > data.conversion.convertibleNow || convert.isPending}
                        onClick={() => convert.mutate(requested)}
                      >
                        Convert to wallet
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>History</Typography>
                {data.history.length === 0 && <Typography variant="body2" color="text.secondary">No activity yet.</Typography>}
                {data.history.map((tx) => (
                  <Box key={tx._id} display="flex" justifyContent="space-between" py={1} borderBottom={1} borderColor="divider">
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{tx.description || tx.type}</Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(tx.createdAt).toLocaleString()}</Typography>
                    </Box>
                    <Typography fontWeight={600} color={tx.points > 0 ? 'success.main' : 'error.main'}>
                      {tx.points > 0 ? '+' : ''}{tx.points}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
